/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import docApi from "api";
import { CacheManager } from "cache";
import { getThumbnailLoadStatus, getThumbnailBitmapSize, sizeOfThumbnailsLoaded } from "selectors/thumbnailSelectors";
import { pagesInView, thumbnailsInView } from "selectors/pagedViewSelectors";
import { isFirstPageLoaded } from "selectors/pagedViewState";
import { sizeOfPagesLoaded } from "selectors/sizeOfPagesLoaded";
import { getPageBitmapSize } from "selectors/pageSelectors";
import { getPageLoadStatus } from "selectors/pageSelectors";
import { getTotalPages, isFullScreenMode } from "selectors/documentSelectors";
import * as constants from "constants/constants";
import { getThumbnailZoomLevelForAJS } from "selectors/thumbnailSelectors";
import { getPageZoomLevel } from "selectors/zoom";
import * as stores from "stores";
import { isIOS } from "dc-preview-utils/src/Utils/DeviceUtils";
import {
  getPagesCacheManagerSingleton,
  setPagesCacheManagerSingleton,
  getThumbnailsCacheManagerSingleton,
  setThumbnailsCacheManagerSingleton
} from "./cacheSingletons";

export const getPagesCacheManager = () => {
  let pagesCacheManeger = getPagesCacheManagerSingleton();
  if (pagesCacheManeger === null && isFirstPageLoaded(stores)) {
    const maxBitmapTotalSize = isIOS() ? constants.MAX_PAGES_CACHE_MEMORY / 4 : constants.MAX_PAGES_CACHE_MEMORY;
    const cacheWindow = isIOS() ? constants.DOCUMENT_OVERSCAN_ROW_COUNT : constants.MAX_PAGES_CACHE_WINDOW;
    pagesCacheManeger = new CacheManager({
      itemType: "page",
      docAPI: docApi,
      stores,
      itemsInView: pagesInView,
      sizeOfItemsLoaded: sizeOfPagesLoaded,
      totalItems: getTotalPages(stores),
      getItemStatus: getPageLoadStatus,
      getItemBitmapSize: getPageBitmapSize,
      getItemZoomLevel: (stores, pageIndex) => {
        let zoomlevel;
        if (isFullScreenMode(stores)) {
          zoomlevel = stores.zoom.getFitPageZoomLevel(pageIndex);
        } else {
          zoomlevel = getPageZoomLevel(stores, pageIndex);
        }
        return zoomlevel;
      },
      loadItem: stores.documentStore.loadPage,
      unloadItem: stores.documentStore.unloadPage,
      revokeItem: stores.documentStore.revokePage,
      maxBitmapTotalSize,
      cacheWindow,
      cachePriority: constants.PAGE_CACHE_PRIORITY
    });
    setPagesCacheManagerSingleton(pagesCacheManeger);
  }
  return pagesCacheManeger;
};

export const getThumbnailsCacheManager = () => {
  let thumbnailsCacheManager = getThumbnailsCacheManagerSingleton();
  if (thumbnailsCacheManager === null && isFirstPageLoaded(stores)) {
    thumbnailsCacheManager = new CacheManager({
      itemType: "thumbnail",
      docAPI: docApi,
      stores,
      itemsInView: thumbnailsInView,
      sizeOfItemsLoaded: sizeOfThumbnailsLoaded,
      totalItems: getTotalPages(stores),
      getItemStatus: getThumbnailLoadStatus,
      getItemBitmapSize: getThumbnailBitmapSize,
      getItemZoomLevel: getThumbnailZoomLevelForAJS,
      loadItem: stores.documentStore.loadThumbnail,
      unloadItem: stores.documentStore.unloadThumbnail,
      revokeItem: stores.documentStore.revokeThumbnail,
      maxBitmapTotalSize: constants.MAX_THUMBNAILS_CACHE_MEMORY,
      cacheWindow: constants.THUMBNAILS_CACHE_WINDOW,
      cachePriority: constants.THUMBNAIL_CACHE_PRIORITY
    });
    setThumbnailsCacheManagerSingleton(thumbnailsCacheManager);
  }
  return thumbnailsCacheManager;
};
